// components/CustomerForm.js
import React, { useState, useEffect } from 'react';
import { Users, ChevronDown, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { customers as initialCustomers } from '../data/customers';

const CustomerForm = ({ customerInfo, setCustomerInfo }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState({});

  // 컴포넌트 마운트 시 localStorage에서 고객 목록 불러오기
  useEffect(() => {
    const savedCustomers = localStorage.getItem('customerList');
    if (savedCustomers) {
      setCustomerList(JSON.parse(savedCustomers));
    } else {
      // 초기 데이터 설정
      setCustomerList(initialCustomers);
      localStorage.setItem('customerList', JSON.stringify(initialCustomers));
    }
  }, []);

  // localStorage에 고객 목록 저장
  const saveCustomersToStorage = (customers) => {
    localStorage.setItem('customerList', JSON.stringify(customers));
    setCustomerList(customers);
  };

  // 고객 선택
  const handleSelectCustomer = (customer) => {
    setCustomerInfo(customer);
    setShowDropdown(false);
  };

  // 고객 등록
  const handleAddCustomer = () => {
    if (!customerInfo.name || !customerInfo.address || !customerInfo.phone) {
      alert('모든 고객 정보를 입력해주세요.');
      return;
    }

    // 중복 확인
    const isDuplicate = customerList.some(customer => 
      customer.name === customerInfo.name && customer.phone === customerInfo.phone
    );

    if (isDuplicate) {
      alert('이미 등록된 고객입니다.');
      return;
    }

    const newCustomer = {
      id: Date.now(),
      ...customerInfo
    };

    const updatedCustomers = [...customerList, newCustomer];
    saveCustomersToStorage(updatedCustomers);
    alert('고객이 등록되었습니다.');
  };

  // 고객 수정 시작
  const handleStartEdit = (customer) => {
    setEditingCustomerId(customer.id);
    setEditingCustomer({ ...customer });
  };

  // 고객 수정 저장
  const handleSaveEdit = () => {
    if (!editingCustomer.name || !editingCustomer.address || !editingCustomer.phone) {
      alert('모든 정보를 입력해주세요.');
      return;
    }

    const updatedCustomers = customerList.map(customer =>
      customer.id === editingCustomerId ? editingCustomer : customer
    );

    saveCustomersToStorage(updatedCustomers);
    setEditingCustomerId(null);
    setEditingCustomer({});
    
    // 현재 선택된 고객이 수정된 고객이면 업데이트
    if (customerInfo.id === editingCustomerId) {
      setCustomerInfo(editingCustomer);
    }
    
    alert('고객 정보가 수정되었습니다.');
  };

  // 고객 수정 취소
  const handleCancelEdit = () => {
    setEditingCustomerId(null);
    setEditingCustomer({});
  };

  // 고객 삭제
  const handleDeleteCustomer = (customerId) => {
    if (window.confirm('정말로 이 고객을 삭제하시겠습니까?')) {
      const updatedCustomers = customerList.filter(customer => customer.id !== customerId);
      saveCustomersToStorage(updatedCustomers);
      
      // 현재 선택된 고객이 삭제된 고객이면 초기화
      if (customerInfo.id === customerId) {
        setCustomerInfo({ name: '', address: '', phone: '' });
      }
      
      alert('고객이 삭제되었습니다.');
    }
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="md:mb-4 lg:mb-10">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium flex items-center">

          고객 정보
        </h3>
        <div className="flex gap-2">
          <button
            onClick={handleAddCustomer}
            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-sm flex items-center gap-1 transition-colors"
          >
            <Plus className="w-3 h-3" />
            등록
          </button>
          {customerList.length > 0 && (
          <div className="relative dropdown-container">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="bg-gray-100 hover:bg-gray-200 border border-gray-300 px-3 py-1 rounded-md text-sm flex items-center gap-1 transition-colors"
            >
              목록 선택
              <ChevronDown className="w-3 h-3" />
            </button>
            {showDropdown && (
              <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                {customerList.map((customer) => (
                  <div key={customer.id} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                    {editingCustomerId === customer.id ? (
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={editingCustomer.name || ''}
                          onChange={(e) => setEditingCustomer({...editingCustomer, name: e.target.value})}
                          className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                          placeholder="고객명"
                        />
                        <input
                          type="text"
                          value={editingCustomer.phone || ''}
                          onChange={(e) => setEditingCustomer({...editingCustomer, phone: e.target.value})}
                          className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                          placeholder="연락처"
                        />
                        <input
                          type="text"
                          value={editingCustomer.address || ''}
                          onChange={(e) => setEditingCustomer({...editingCustomer, address: e.target.value})}
                          className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                          placeholder="주소"
                        />

                        <div className="flex gap-1">
                          <button
                            onClick={handleSaveEdit}
                            className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs flex items-center gap-1"
                          >
                            <Save className="w-3 h-3" />
                            저장
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs flex items-center gap-1"
                          >
                            <X className="w-3 h-3" />
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => handleSelectCustomer(customer)}
                        >
                          <div className="font-medium">{customer.name}</div>
                          <div className="text-sm text-gray-500">{customer.phone}</div>
                          <div className="text-xs text-gray-400">{customer.address}</div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEdit(customer);
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-2 rounded text-xs flex items-center gap-1"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomer(customer.id);
                            }}
                            className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs flex items-center gap-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
      <div className="space-y-3">
        <input
          type="text"
          placeholder="고객명/업체명"
          value={customerInfo.name}
          onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <input
          type="text"
          placeholder="주소"
          value={customerInfo.address}
          onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <input
          type="text"
          placeholder="연락처"
          value={customerInfo.phone}
          onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
       
      </div>
    </div>
  );
};

export default CustomerForm;